import { Vec3 } from '@/core/math/Vec3';

/**
 * Acceleration as a function of state. Must be pure with respect to the
 * supplied position/velocity so the multi-stage integrators are valid.
 */
export type AccelerationFn = (position: Vec3, velocity: Vec3, t: number, out: Vec3) => Vec3;

export interface KinematicState {
  position: Vec3;
  velocity: Vec3;
}

const scratchA = new Vec3();
const scratchB = new Vec3();
const scratchV = new Vec3();

/**
 * Semi-implicit (symplectic) Euler.
 *
 * Cheap and energy-stable enough for local rigid bodies and characters, where
 * the step is small relative to the dynamics and drift over minutes does not
 * matter. Never used for orbits.
 */
export function integrateSemiImplicitEuler(
  state: KinematicState,
  accel: AccelerationFn,
  t: number,
  dt: number,
): void {
  accel(state.position, state.velocity, t, scratchA);
  state.velocity.addScaled(scratchA, dt);
  state.position.addScaled(state.velocity, dt);
}

/**
 * Velocity Verlet.
 *
 * The default for vessels. It is symplectic for position-dependent forces, so a
 * coasting orbit does not spiral in or out the way explicit Euler does, and it
 * costs only two acceleration evaluations per step.
 *
 * Velocity-dependent forces (drag) are handled by evaluating the second stage
 * with a predicted velocity. That breaks strict symplecticity, but only where
 * an atmosphere is present, and there the trajectory is dissipative anyway.
 */
export function integrateVelocityVerlet(state: KinematicState, accel: AccelerationFn, t: number, dt: number): void {
  accel(state.position, state.velocity, t, scratchA);

  // x(t+dt) = x + v*dt + 0.5*a*dt^2
  state.position.addScaled(state.velocity, dt).addScaled(scratchA, 0.5 * dt * dt);

  // Predicted velocity for the velocity-dependent part of the force.
  scratchV.copy(state.velocity).addScaled(scratchA, dt);
  accel(state.position, scratchV, t + dt, scratchB);

  // v(t+dt) = v + 0.5*(a(t) + a(t+dt))*dt
  state.velocity.addScaled(scratchA, 0.5 * dt).addScaled(scratchB, 0.5 * dt);
}

/**
 * Classical fourth-order Runge-Kutta.
 *
 * Used for trajectory *prediction* rather than for the live simulation: it is
 * far more accurate per step, which lets the predictor take large steps when
 * sketching an orbit tens of minutes ahead. It is not symplectic, so it is a
 * poor choice for integrating the same orbit for hours.
 */
export function integrateRK4(state: KinematicState, accel: AccelerationFn, t: number, dt: number): void {
  const p0 = state.position.clone();
  const v0 = state.velocity.clone();

  const a1 = accel(p0, v0, t, new Vec3()).clone();
  const k1p = v0.clone();

  const p2 = p0.clone().addScaled(k1p, dt / 2);
  const v2 = v0.clone().addScaled(a1, dt / 2);
  const a2 = accel(p2, v2, t + dt / 2, new Vec3()).clone();

  const p3 = p0.clone().addScaled(v2, dt / 2);
  const v3 = v0.clone().addScaled(a2, dt / 2);
  const a3 = accel(p3, v3, t + dt / 2, new Vec3()).clone();

  const p4 = p0.clone().addScaled(v3, dt);
  const v4 = v0.clone().addScaled(a3, dt);
  const a4 = accel(p4, v4, t + dt, new Vec3()).clone();

  state.position
    .copy(p0)
    .addScaled(v0, dt / 6)
    .addScaled(v2, dt / 3)
    .addScaled(v3, dt / 3)
    .addScaled(v4, dt / 6);

  state.velocity
    .copy(v0)
    .addScaled(a1, dt / 6)
    .addScaled(a2, dt / 3)
    .addScaled(a3, dt / 3)
    .addScaled(a4, dt / 6);
}

/**
 * Fixed-timestep accumulator with a hard cap on catch-up work.
 *
 * The cap is not optional polish: without it a single long frame (a tab losing
 * focus, a shader compile) queues hundreds of physics steps, which takes even
 * longer, which queues more steps. The simulation is allowed to fall behind
 * wall-clock time instead.
 */
export class FixedStepAccumulator {
  private accumulator = 0;
  /** Fraction through the current step, for render interpolation. */
  alpha = 0;
  /** Steps dropped in the last update because the cap was hit. */
  droppedSteps = 0;

  constructor(
    public readonly stepSize = 1 / 60,
    public maxStepsPerFrame = 6,
  ) {}

  /**
   * Feeds in a wall-clock delta and returns how many fixed steps to run.
   * Call `consume()` in a loop and stop when it returns false.
   */
  advance(realDeltaSeconds: number): number {
    this.accumulator += Math.max(0, realDeltaSeconds);
    let steps = Math.floor(this.accumulator / this.stepSize);
    this.droppedSteps = 0;
    if (steps > this.maxStepsPerFrame) {
      this.droppedSteps = steps - this.maxStepsPerFrame;
      // Discard the backlog rather than trying to catch up.
      this.accumulator -= this.droppedSteps * this.stepSize;
      steps = this.maxStepsPerFrame;
    }
    this.accumulator -= steps * this.stepSize;
    this.alpha = this.accumulator / this.stepSize;
    return steps;
  }

  reset(): void {
    this.accumulator = 0;
    this.alpha = 0;
    this.droppedSteps = 0;
  }
}
